import { formatCurrency } from "@/lib/currency";
/**
 * ChoosePlanScreen — iOS native paywall
 *
 * Loads RevenueCat offerings and shows subscription cards.
 * Handles purchase, restore, and proper error states.
 * NEVER shows an infinite spinner — always resolves to cards or error.
 * 
 * Retries offerings up to 3 times before showing fallback.
 * Includes full Apple-required subscription disclosures.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Loader2, RotateCcw, Truck, Shield, Crown, AlertCircle, RefreshCw, ArrowLeft, Settings2 } from "lucide-react";
import { APPLE_SUBSCRIPTION_MANAGEMENT_URL } from "@/lib/billingAccess";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  getOfferings,
  purchasePackage,
  restorePurchases,
  getSubscriptionStatus,
  initRevenueCat,
  logoutRevenueCat,
  checkIntroEligibility,
  PRODUCT_TO_TIER,
  type RCPackage,
  type IntroEligibility,
} from "@/services/revenuecat";
import { SUBSCRIPTION_TIERS } from "@/lib/subscriptionTiers";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  onSubscriptionActivated: (productId?: string) => void;
}

const TIER_ICONS: Record<string, typeof Truck> = {
  starter: Truck,
  growth: Shield,
  pro: Crown,
};

const TIER_FEATURES: Record<string, string[]> = {
  starter: [
    "Up to 75 jobs per month",
    "Invoicing & PDF generation",
    "GPS tracking & proof of delivery",
    "Customer management",
  ],
  growth: [
    "Up to 200 jobs per month",
    "Everything in Starter",
    "AI damage detection",
    "Priority support",
  ],
  pro: [
    "Unlimited jobs with AI features",
    "Everything in Growth",
    "Multi-driver management",
    "Advanced analytics",
  ],
};

const OFFERINGS_TIMEOUT_MS = 8_000;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2_000;

export function ChoosePlanScreen({ onSubscriptionActivated }: Props) {
  const [packages, setPackages] = useState<RCPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const { markManualLogout, session } = useAuth();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  console.log("[ChoosePlanScreen] mounted");

  const loadOfferingsOnce = useCallback(async (): Promise<RCPackage[]> => {
    return new Promise(async (resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("timeout"));
      }, OFFERINGS_TIMEOUT_MS);

      try {
        // Ensure RevenueCat is initialized before fetching offerings
        const initOk = await initRevenueCat();
        console.log("[ChoosePlanScreen] initRevenueCat result:", initOk);
        
        const pkgs = await getOfferings();
        clearTimeout(timeoutId);
        resolve(pkgs);
      } catch (e) {
        clearTimeout(timeoutId);
        reject(e);
      }
    });
  }, []);

  const loadOfferings = useCallback(async () => {
    console.log("[ChoosePlanScreen] load offerings start");
    setLoading(true);
    setError(null);
    setRetryCount(0);

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      if (!mountedRef.current) return;
      console.log(`[ChoosePlanScreen] attempt ${attempt}/${MAX_RETRIES}`);
      setRetryCount(attempt);

      try {
        const pkgs = await loadOfferingsOnce();
        if (!mountedRef.current) return;
        console.log("[ChoosePlanScreen] Loaded", pkgs.length, "packages");

        if (pkgs.length > 0) {
          // Check intro eligibility before showing cards
          const enriched = await checkIntroEligibility(pkgs);
          if (!mountedRef.current) return;
          setPackages(enriched);
          setLoading(false);
          setError(null);
          return;
        }

        // Got 0 packages, retry if we have attempts left
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
      } catch (e: any) {
        console.error(`[ChoosePlanScreen] attempt ${attempt} failed:`, e);
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
          continue;
        }
      }
    }

    // All retries exhausted
    if (!mountedRef.current) return;
    setLoading(false);
    setError("Subscriptions are temporarily unavailable. Please try again.");
    console.log("[ChoosePlanScreen] all retries exhausted");
  }, [loadOfferingsOnce]);

  useEffect(() => {
    loadOfferings();
  }, [loadOfferings]);

  // ── Sync purchase to backend (safety net if webhook fails) ──
  const syncPurchaseToBackend = async (productId: string) => {
    try {
      console.log("[ChoosePlanScreen] Syncing purchase to backend...", productId);
      const status = await getSubscriptionStatus();
      await supabase.functions.invoke("sync-apple-subscription", {
        body: {
          product_id: status.productId || productId,
          is_active: status.isActive ?? true,
          expiration_date: status.expirationDate,
          is_sandbox: status.isSandbox,
          will_renew: status.willRenew,
          period_type: status.periodType,
        },
      });
      console.log("[ChoosePlanScreen] ✅ Backend sync successful");
    } catch (syncErr) {
      console.warn("[ChoosePlanScreen] Backend sync failed (non-fatal, webhook should handle):", syncErr);
    }
  };

  // ── Purchase ──────────────────────────────────────────────
  const handlePurchase = async (pkg: RCPackage) => {
    const tierKey = PRODUCT_TO_TIER[pkg.productId] || "starter";
    const tierConfig = SUBSCRIPTION_TIERS[tierKey as keyof typeof SUBSCRIPTION_TIERS];
    const displayPrice = `${formatCurrency(tierConfig?.price ?? pkg.price)}`;
    const showTrial = pkg.introEligibility === "eligible" && pkg.introOffer !== null;

    // Debug log before purchase
    console.log(
      `[ChoosePlanScreen] 🛒 PRE-PURCHASE DEBUG:\n` +
      `  selectedPackageId: ${pkg.productId}\n` +
      `  localizedPrice: ${pkg.priceString}\n` +
      `  introOfferPresent: ${pkg.introOffer ? "yes" : "no"}\n` +
      `  introOfferDuration: ${pkg.introOffer?.periodLabel ?? "N/A"}\n` +
      `  introEligibility: ${pkg.introEligibility}\n` +
      `  showTrial: ${showTrial}`
    );

    // Show confirmation toast before Apple sheet opens
    const confirmMessage = showTrial
      ? "You won't be charged today. Your 30-day free trial will start."
      : `You will be charged ${displayPrice} today and your subscription will renew monthly.`;

    toast.info("Confirm Purchase", { description: confirmMessage, duration: 4000 });

    setPurchasing(pkg.productId);
    try {
      console.log("[ChoosePlanScreen] Starting purchase for:", pkg.productId);
      const result = await purchasePackage(pkg);
      console.log("[ChoosePlanScreen] Purchase result:", JSON.stringify(result));

      if (result.error === "cancelled") {
        console.log("[ChoosePlanScreen] User cancelled purchase");
        return;
      }

      if (result.success) {
        toast.success("Subscription activated!", {
          description: "Welcome to TransportMate. You're all set.",
        });
        // Sync to backend immediately — don't wait for webhook
        await syncPurchaseToBackend(pkg.productId);
        console.log("[ChoosePlanScreen] Calling onSubscriptionActivated…");
        onSubscriptionActivated(pkg.productId);
        return;
      }

      toast.error("Purchase failed", {
        description: result.error || "Please try again.",
      });
    } finally {
      setPurchasing(null);
    }
  };

  // ── Manage Apple subscription (works for lapsed subscribers too) ──
  const handleManageAppleSubscription = async () => {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: APPLE_SUBSCRIPTION_MANAGEMENT_URL });
    } catch {
      toast.error("Please manage your subscription in Settings → Apple ID → Subscriptions");
    }
  };

  // ── Restore ───────────────────────────────────────────────
  const handleRestore = async () => {
    if (!session?.user) {
      toast.info("Please log in first", {
        description: "You need to be signed in to restore a previous purchase.",
      });
      return;
    }
    setRestoring(true);
    try {
      console.log("[ChoosePlanScreen] Starting restore…");
      const result = await restorePurchases();
      console.log("[ChoosePlanScreen] Restore result:", JSON.stringify(result));

      if (result.success && result.isActive) {
        toast.success("Subscription restored!", {
          description: "Your previous subscription has been restored.",
        });
        // Sync restored purchase to backend
        try {
          const subStatus = await getSubscriptionStatus();
          if (subStatus.productId) {
            await syncPurchaseToBackend(subStatus.productId);
          }
        } catch (_) {}
        console.log("[ChoosePlanScreen] ✅ Calling onSubscriptionActivated from restore");
        onSubscriptionActivated();
      } else if (result.success && !result.isActive) {
        toast.info("No active subscription found", {
          description: "No previous purchase was found for this account.",
        });
      } else {
        toast.error("Restore failed", {
          description: result.error || "Please try again.",
        });
      }
    } catch (e: any) {
      console.error("[ChoosePlanScreen] Restore error:", e);
      toast.error("Restore failed", {
        description: e?.message || "Please try again.",
      });
    } finally {
      setRestoring(false);
    }
  };

  // ── Sign out ──────────────────────────────────────────────
  const handleLogout = async () => {
    markManualLogout();
    await logoutRevenueCat();
    await supabase.auth.signOut();
  };

  // ── Loading state ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-4">
        {session && (
          <button
            onClick={handleLogout}
            className="absolute top-4 left-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors safe-area-inset-padding"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Loading subscription options…</p>
        {retryCount > 1 && (
          <p className="text-xs text-muted-foreground/60">
            Attempt {retryCount} of {MAX_RETRIES}…
          </p>
        )}
      </div>
    );
  }

  // ── Error state — visible, no spinner ─────────────────────
  if (error && packages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="text-center text-muted-foreground">{error}</p>
        <Button onClick={loadOfferings} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Retry
        </Button>
        {session && (
          <Button variant="ghost" size="sm" onClick={handleRestore} className="text-muted-foreground mt-2">
            <RotateCcw className="h-4 w-4 mr-2" />
            Restore Purchases
          </Button>
        )}
        {session && (
          <Button variant="link" size="sm" onClick={handleLogout} className="text-muted-foreground text-xs mt-1">
            Sign out
          </Button>
        )}
      </div>
    );
  }

  // Sort by price ascending
  const sortedPackages = [...packages].sort((a, b) => a.price - b.price);

  return (
    <div className="min-h-screen bg-background safe-area-inset-padding">
      <div className="max-w-md mx-auto px-4 py-8 space-y-6">
        {/* Back / Sign out button */}
        {session && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground -mb-2 px-0"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Button>
        )}
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <Truck className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Choose Your Plan</h1>
          <p className="text-muted-foreground text-sm">
            Subscribe to unlock TransportMate and manage your transport jobs.
          </p>
        </div>

        {/* Plan descriptions */}
        <div className="bg-muted/50 rounded-xl p-4 space-y-2">
          <p className="text-sm font-medium">Plan Details:</p>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• <strong>Starter Plan</strong> – 75 jobs per month</p>
            <p>• <strong>Growth Plan</strong> – 200 jobs per month</p>
            <p>• <strong>Pro Plan</strong> – Unlimited jobs with AI features</p>
          </div>
        </div>

        {/* Cards */}
        <div className="space-y-3">
          {sortedPackages.map((pkg) => {
            const tierKey = PRODUCT_TO_TIER[pkg.productId] || "starter";
            const tierConfig = SUBSCRIPTION_TIERS[tierKey as keyof typeof SUBSCRIPTION_TIERS];
            const Icon = TIER_ICONS[tierKey] || Truck;
            const features = TIER_FEATURES[tierKey] || [];
            const isPopular = tierKey === "growth";
            const isPurchasing = purchasing === pkg.productId;

            const displayPrice = `${formatCurrency(tierConfig?.price ?? pkg.price)}`;
            
            // Determine if this user is eligible for the intro offer on THIS package
            const showTrial = pkg.introEligibility === "eligible" && pkg.introOffer !== null;
            
            // Build pricing summary based on actual eligibility
            const pricingSummary = showTrial
              ? `Free for ${pkg.introOffer!.periodLabel}, then ${displayPrice}/month`
              : `${displayPrice} charged today, then renews monthly`;

            const ctaLabel = showTrial
              ? `Start Free Trial — then ${displayPrice}/mo`
              : `Subscribe Now — ${displayPrice}/mo`;

            return (
              <Card
                key={pkg.productId}
                className={isPopular ? "border-primary ring-2 ring-primary/20" : ""}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">
                        {tierConfig?.name || pkg.localizedTitle}
                      </CardTitle>
                    </div>
                    <span className="font-bold text-lg">{formatCurrency(tierConfig?.price ?? pkg.price)}/mo</span>
                  </div>
                  {isPopular && (
                    <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full w-fit">
                      Most Popular
                    </span>
                  )}
                  {/* Pricing summary based on intro eligibility */}
                  <p className="text-xs font-medium text-muted-foreground px-1">
                    {pricingSummary}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2">
                  {features.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-primary shrink-0" />
                      <span>{f}</span>
                    </div>
                  ))}
                  <Button
                    className="w-full mt-3"
                    variant={isPopular ? "default" : "outline"}
                    disabled={purchasing !== null}
                    onClick={() => handlePurchase(pkg)}
                  >
                    {isPurchasing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing…
                      </>
                    ) : (
                      ctaLabel
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Restore + Legal */}
        <div className="text-center space-y-3 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRestore}
            disabled={restoring}
            className="text-muted-foreground"
          >
            {restoring ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RotateCcw className="h-4 w-4 mr-2" />
            )}
            Restore Purchases
          </Button>

          {/* Apple subscription management only — never Stripe on iOS */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManageAppleSubscription}
            className="text-muted-foreground"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Manage Apple Subscription
          </Button>

          {/* Apple-required subscription disclosure */}
          <div className="text-xs text-muted-foreground px-4 space-y-2">
            <p>
              Payment will be charged to your Apple ID account at confirmation of purchase.
            </p>
            <p>
              Subscriptions automatically renew unless canceled at least 24 hours before the end of the current period.
            </p>
            <p>
              Your account will be charged for renewal within 24 hours prior to the end of the current period.
            </p>
            <p>
              You can manage and cancel subscriptions in your Apple ID account settings.
            </p>
          </div>

          <div className="flex justify-center gap-4 text-xs text-muted-foreground">
            <a href="https://transportmate.co.uk/privacy" className="underline">
              Privacy Policy
            </a>
            <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" className="underline">
              Terms of Use (EULA)
            </a>
          </div>

          {session && (
            <Button
              variant="link"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground text-xs"
            >
              Sign out
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
